import { useEffect, useRef, useState } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import getUserMenuItems from "./AdminMenuContent";
import AdminSideBar from "./AdminSideBar";
import { adminReqConfig, api } from "../../include/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminFooter from "../common/AdminFooter";
import { useNotification } from "../../Toast/ToastProvider";
import { ERROR, SUCCESS } from "../../include/constant";
import { ChevronDown, X } from "react-feather";
import Papa from "papaparse";
import "../../styles/CategoryProductList.css";
import { formatShortMonthDateYearWithTime, getAdmin } from "../../include/function";

const ListPincode = () => {
  const navigate = useNavigate();
  const homeLink = "/admin";
  const [fetchLoading, setFetchLoading] = useState(true);
  const [pincodeList, setPincodeList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();
  const toast = useNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const user = getAdmin();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const filterWrapperRef = useRef(null);

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setSearchQuery(q);
  }, [searchParams]);

  const [pincodePermissions, setPincodePermissions] = useState({
    add: false,
    view: false,
    edit: false,
    delete: false,
  });

  useEffect(() => {
    const fetchPermissions = async () => {
      if (user?.createdBy === "super") {
        setPincodePermissions({
          add: true,
          view: true,
          edit: true,
          delete: true,
        });
        return;
      }
      if (!user?.roleId) return;

      try {
        const res = await api.get(
          `/role_permission/?roleId=${user.roleId}`,
          adminReqConfig(),
        );
        if (res.status === 200 && Array.isArray(res.data.data)) {
          const perm = res.data.data.find(
            (p) =>
              p.pageTitle === "Pincode" || p.pageTitle === "pincode/List",
          );

          if (perm) {
            setPincodePermissions({
              add: perm.add === "true" || perm.add === 1,
              view: perm.view === "true" || perm.view === 1,
              edit: perm.edit === "true" || perm.edit === 1,
              delete: perm.delete === "true" || perm.delete === 1,
            });
          }
        }
      } catch (err) {
        console.warn("Failed to fetch pincode permissions", err);
      }
    };

    fetchPermissions();
  }, [user?.roleId, user?.createdBy]);

  const getPincode = () => {
    setFetchLoading(true);
    api
      .get("/pincode/", adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          const allPincodes = response.data.data || [];
          setPincodeList(allPincodes);
          setFilteredData(allPincodes);
        }
      })
      .catch(() => {
        setPincodeList([]);
        setFilteredData([]);
      })
      .finally(() => setFetchLoading(false));
  };

  useEffect(() => {
    getPincode();
  }, []);

  useEffect(() => {
    let result = [...pincodeList];
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter(
        (item) =>
          String(item.pincode || "").toLowerCase().includes(query) ||
          String(item.id || "").includes(query),
      );
    }
    setFilteredData(result);
    setCurrentPage(1);
  }, [searchQuery, pincodeList, searchParams]);

  let currentItems = [];
  let totalPage = 1;
  let showingFrom = 0;
  let showingTo = 0;
  if (itemsPerPage === "all") {
    currentItems = filteredData;
    showingFrom = filteredData.length > 0 ? 1 : 0;
    showingTo = filteredData.length;
  } else {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    totalPage = Math.ceil(filteredData.length / itemsPerPage) || 1;
    showingFrom = filteredData.length > 0 ? indexOfFirstItem + 1 : 0;
    showingTo = Math.min(indexOfLastItem, filteredData.length);
  }
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleView = (id) => {
    navigate(`/admin/pincode/pincodeAddNew?id=${id}&view=true`);
  };
  const handleEdit = (id) => {
    navigate(`/admin/pincode/pincodeAddNew?id=${id}`);
  };
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this pincode?")) {
      api
        .delete(`/pincode/?id=${id}`, adminReqConfig())
        .then((response) => {
          if (response) {
            toast({ type: SUCCESS, message: response.data.message });
            setPincodeList((prev) => prev.filter((p) => p.id !== id));
            setFilteredData((prev) => prev.filter((p) => p.id !== id));
          }
        })
        .catch(() => {
          toast({ type: ERROR, message: "Failed to delete pincode" });
        });
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = currentItems.map((p) => p.id);
      setSelectedData((prev) => [...new Set([...prev, ...allIds])]);
    } else {
      const idsToRemove = currentItems.map((p) => p.id);
      setSelectedData((prev) => prev.filter((id) => !idsToRemove.includes(id)));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedData((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id],
    );
  };

  const handleDownloadCSV = (exportScope = "current") => {
    let dataToExport = [];
    if (selectedData.length > 0) {
      dataToExport = pincodeList.filter((item) => selectedData.includes(item.id));
    } else if (exportScope === "all") {
      dataToExport = pincodeList;
    } else {
      dataToExport = currentItems;
    }

    if (dataToExport.length === 0) {
      toast({ type: ERROR, message: "No pincode data to download" });
      return;
    }

    const columns = ["S.No", "ID", "Pincode", "Created Timestamp"];
    const csvData = dataToExport.map((item, index) => ({
      "S.No": index + 1,
      ID: item.id || "",
      Pincode: item.pincode || "",
      "Created Timestamp": item.createTimestamp || "",
    }));

    const csv = Papa.unparse({
      fields: columns,
      data: csvData,
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fileName =
      selectedData.length > 0
        ? `Pincode_Selected_${selectedData.length}.csv`
        : exportScope === "all"
          ? "Pincode_All.csv"
          : `Pincode_Page${currentPage}.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const actionButtonStyle = {
    backgroundColor: "#D9EBFF",
    height: "39px",
    width: "50px",
    border: "none",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    padding: "0",
    margin: "0",
    fontSize: "14px",
    lineHeight: 1,
    color: "inherit",
    outline: "none",
  };

  return (
    <>
      <AdminSideBar
        menuContent={menuContent}
        isMinimized={isSidebarMinimized}
      />
      <AdminTopNavBar
        toggleSidebar={toggleSidebar}
        isSidebarMinimized={isSidebarMinimized}
        homeLink={homeLink}
        title="PINCODE"
      />
      <div
        className="content-wrapper p-0"
        style={{
          marginLeft: isSidebarMinimized ? "80px" : "250px",
          flex: "1",
        }}
      >
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
            minHeight: "80vh",
          }}
        >
          {fetchLoading ? (
            <p>Loading pincodes...</p>
          ) : (
            <>
              <div className="rounded-5 bg-white py-24 ">
                <div className="row align-items-center px-24 py-10 mb-3">
                  <div className="col-auto">
                    <h6 className="mb-0">All Pincode List</h6>
                  </div>
                  <div className="col">
                    <div
                      className="filter-wrapper"
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "500px",
                      }}
                      ref={filterWrapperRef}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid #c6d6f5",
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          width: "100%",
                        }}
                      >
                        <img
                          src="/assets/images/search-icon.png"
                          alt="Search"
                          style={{ width: "18px", marginLeft: "8px" }}
                        />
                        <input
                          type="text"
                          placeholder="Search pincode..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          style={{
                            border: "none",
                            outline: "none",
                            padding: "8px 8px",
                            width: "100%",
                            fontSize: "14px",
                            color: "#333",
                            backgroundColor: "transparent",
                          }}
                        />
                        {searchQuery && (
                          <X
                            size={24}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchQuery("");
                              setCurrentPage(1);
                            }}
                            style={{
                              position: "absolute",
                              right: "12px",
                              cursor: "pointer",
                              color: "#999",
                              padding: "4px",
                              zIndex: 10,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-auto d-flex align-items-center gap-2">
                    {pincodePermissions.add && (
                      <button
                        className="btn me-0 mb-0"
                        style={{
                          height: "40px",
                          width: "140px",
                          color: "white",
                          backgroundColor: "#2696D5",
                          border: "none",
                          borderRadius: "6px",
                        }}
                        onClick={() => navigate("/admin/pincode/pincodeAddNew")}
                      >
                        Add New
                      </button>
                    )}
                    <CustomDropdown handleDownloadCSV={handleDownloadCSV} />
                  </div>
                </div>

                <div className="table-responsive mt-24 product-table">
                  <table className="table table-striped custom-striped-table-product-list align-middle">
                    <thead>
                      <tr>
                        <th scope="col" style={{ width: "50px" }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            style={{ borderColor: "#c5d5f5" }}
                            onChange={handleSelectAll}
                            checked={
                              currentItems.length > 0 &&
                              currentItems.every((p) =>
                                selectedData.includes(p.id),
                              )
                            }
                          />
                        </th>
                        <th scope="col" style={{ fontSize: "15px" }}>
                          ID
                        </th>
                        <th scope="col" style={{ fontSize: "15px" }}>
                          Pincode
                        </th>
                        <th scope="col" style={{ fontSize: "15px" }}>
                          Created Timestamp
                        </th>
                        <th
                          scope="col"
                          style={{
                            width: "180px",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <input
                                type="checkbox"
                                style={{ borderColor: "#c5d5f5" }}
                                className="form-check-input"
                                checked={selectedData.includes(item.id)}
                                onChange={() => handleSelectOne(item.id)}
                              />
                            </td>
                            <td>{item.id}</td>
                            <td>{item.pincode}</td>
                            <td>
                              {formatShortMonthDateYearWithTime(
                                item.createTimestamp,
                              )}
                            </td>
                            <td style={{ position: "relative" }}>
                              <div className="d-flex gap-1 justify-content-center">
                                {pincodePermissions.view && (
                                  <button
                                    style={actionButtonStyle}
                                    onClick={() => handleView(item.id)}
                                  >
                                    <img
                                      src="/assets/images/view-icon.png"
                                      alt="View"
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        objectFit: "contain",
                                      }}
                                      onError={(e) =>
                                        (e.target.style.display = "none")
                                      }
                                    />
                                  </button>
                                )}
                                {pincodePermissions.edit && (
                                  <button
                                    style={actionButtonStyle}
                                    onClick={() => handleEdit(item.id)}
                                  >
                                    <img
                                      src="/assets/images/edit-icon.png"
                                      alt="Edit"
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        objectFit: "contain",
                                      }}
                                      onError={(e) =>
                                        (e.target.style.display = "none")
                                      }
                                    />
                                  </button>
                                )}
                                {pincodePermissions.delete && (
                                  <button
                                    style={actionButtonStyle}
                                    onClick={() => handleDelete(item.id)}
                                  >
                                    <img
                                      src="/assets/images/delete-icon.png"
                                      alt="Delete"
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        objectFit: "contain",
                                      }}
                                      onError={(e) =>
                                        (e.target.style.display = "none")
                                      }
                                    />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            No data found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-16 p-16">
                  <div className="d-flex align-items-center">
                    <select
                      className="form-select d-inline-block w-auto"
                      value={itemsPerPage}
                      onChange={(e) => {
                        const val = e.target.value;
                        setItemsPerPage(val === "all" ? "all" : parseInt(val));
                        setCurrentPage(1);
                      }}
                      style={{
                        height: "34px",
                        fontSize: "14px",
                        minWidth: "70px",
                      }}
                    >
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="all">All</option>
                    </select>
                    <span className="ms-2">
                      Showing {showingFrom} to {showingTo} of{" "}
                      {filteredData.length} Lists
                    </span>
                  </div>
                  <nav>
                    <ul className="pagination mt-0">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage - 1)}
                        >
                          &laquo;
                        </button>
                      </li>
                      {(() => {
                        const pagesToShow = 5;
                        let startPage = Math.max(
                          1,
                          currentPage - Math.floor(pagesToShow / 2),
                        );
                        let endPage = startPage + pagesToShow - 1;
                        if (endPage > totalPage) {
                          endPage = totalPage;
                          startPage = Math.max(1, endPage - pagesToShow + 1);
                        }
                        const pageNumbers = [];
                        for (let i = startPage; i <= endPage; i++) {
                          pageNumbers.push(i);
                        }
                        return pageNumbers.map((pageNum) => (
                          <li
                            key={pageNum}
                            className={`page-item ${
                              currentPage === pageNum ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => paginate(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        ));
                      })()}
                      <li
                        className={`page-item ${
                          currentPage === totalPage ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage + 1)}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </>
          )}
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </section>
      </div>
    </>
  );
};

export default ListPincode;

const CustomDropdown = ({ handleDownloadCSV }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState("Options");
  const dropdownRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const options = [
    { label: "Export", value: "export" },
    { label: "Export All", value: "exportAll" },
  ];
  const handleSelect = (opt) => {
    setSelected(opt.label);
    setIsOpen(false);
    if (opt.value === "export") {
      handleDownloadCSV("current");
    } else if (opt.value === "exportAll") {
      handleDownloadCSV("all");
    }
    setSelected("Options");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      className="custom-select"
      style={{ position: "relative", display: "inline-block" }}
      ref={dropdownRef}
    >
      <div
        className="select-header"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor: isHovered ? "#EDF8FA" : "#fff",
          color: "rgba(129, 134, 134, 1)",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          cursor: "pointer",
          width: "140px",
          height: "40px",
          textAlign: "left",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 500,
          transition: "background-color 0.2s ease",
        }}
      >
        <span>{selected}</span>
        <ChevronDown />
      </div>
      {isOpen && (
        <ul
          className="select-options"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            listStyle: "none",
            margin: 0,
            padding: 0,
            backgroundColor: "white",
            border: "none",
            borderRadius: "4px",
            zIndex: 1000,
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            fontWeight: 500,
          }}
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              onClick={() => handleSelect(opt)}
              className="select-option"
              style={{
                padding: "8px 12px",
                color: "rgba(129, 134, 134, 1)",
                backgroundColor: "white",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
